import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProfanityTermService } from '../../../../../services/profanity-term.service';
import { ProfanityTerm } from '../../../../../models/profanity-term.model';

@Component({
  selector: 'app-word-filter',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './word-filter.html',
  styleUrl: './word-filter.css',
})
export class WordFilter implements OnInit {
  private termService = inject(ProfanityTermService);
  private cdr = inject(ChangeDetectorRef);

  terms: ProfanityTerm[] = [];
  loading = true;
  error = '';

  searchQuery = '';
  statusFilter: 'all' | 'active' | 'inactive' = 'all';
  letterFilter = 'all';
  readonly alphabet = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

  pageSize = 50;
  currentPage = 1;

  showAddForm = false;
  newTerm = '';
  newCategory = '';
  addError = '';
  saving = false;

  editingId: number | null = null;
  editTerm = '';
  editCategory = '';
  editError = '';

  confirmDeleteId: number | null = null;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.termService.getTerms().subscribe({
      next: (terms) => {
        this.terms = terms;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = 'Failed to load blocked terms.';
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  get filteredTerms(): ProfanityTerm[] {
    const query = this.searchQuery.trim().toLowerCase();

    return this.terms.filter((t) => {
      const matchesQuery =
        !query || t.term.includes(query) || (t.category ?? '').toLowerCase().includes(query);
      const matchesStatus =
        this.statusFilter === 'all' ||
        (this.statusFilter === 'active' && t.is_active) ||
        (this.statusFilter === 'inactive' && !t.is_active);
      const matchesLetter =
        this.letterFilter === 'all' ||
        (this.letterFilter === '#'
          ? !/^[a-z]/i.test(t.term)
          : t.term.toLowerCase().startsWith(this.letterFilter.toLowerCase()));

      return matchesQuery && matchesStatus && matchesLetter;
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredTerms.length / this.pageSize));
  }

  get pagedTerms(): ProfanityTerm[] {
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredTerms.slice(start, start + this.pageSize);
  }

  resetPage(): void {
    this.currentPage = 1;
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    this.newTerm = '';
    this.newCategory = '';
    this.addError = '';
  }

  addTerm(): void {
    const term = this.newTerm.trim();
    if (!term) {
      this.addError = 'A term is required.';
      return;
    }

    this.saving = true;
    this.addError = '';
    this.termService.createTerm({ term, category: this.newCategory.trim() || null }).subscribe({
      next: (created) => {
        this.terms = [...this.terms, created].sort((a, b) => a.term.localeCompare(b.term));
        this.saving = false;
        this.showAddForm = false;
        this.newTerm = '';
        this.newCategory = '';
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.addError = err?.error?.message || 'Failed to add term.';
        this.saving = false;
        this.cdr.detectChanges();
      },
    });
  }

  startEdit(term: ProfanityTerm): void {
    this.confirmDeleteId = null;
    this.editingId = term.id;
    this.editTerm = term.term;
    this.editCategory = term.category ?? '';
    this.editError = '';
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editError = '';
  }

  saveEdit(term: ProfanityTerm): void {
    const newTerm = this.editTerm.trim();
    if (!newTerm) {
      this.editError = 'A term is required.';
      return;
    }

    this.termService
      .updateTerm(term.id, { term: newTerm, category: this.editCategory.trim() || null })
      .subscribe({
        next: (updated) => {
          this.terms = this.terms
            .map((t) => (t.id === updated.id ? updated : t))
            .sort((a, b) => a.term.localeCompare(b.term));
          this.editingId = null;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.editError = err?.error?.message || 'Failed to update term.';
          this.cdr.detectChanges();
        },
      });
  }

  toggleActive(term: ProfanityTerm): void {
    this.termService.updateStatus(term.id, !term.is_active).subscribe({
      next: (updated) => {
        this.terms = this.terms.map((t) => (t.id === updated.id ? updated : t));
        this.cdr.detectChanges();
      },
    });
  }

  confirmDelete(term: ProfanityTerm): void {
    this.editingId = null;
    this.confirmDeleteId = term.id;
  }

  cancelDelete(): void {
    this.confirmDeleteId = null;
  }

  deleteTerm(term: ProfanityTerm): void {
    this.termService.deleteTerm(term.id).subscribe({
      next: () => {
        this.terms = this.terms.filter((t) => t.id !== term.id);
        this.confirmDeleteId = null;
        this.cdr.detectChanges();
      },
    });
  }
}
